/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useState, useCallback } from "react";
import { CellChange, DefaultCellTypes } from "@silevis/reactgrid";
import { applyChangesToData, setTargetCellError } from "../utils";
import { ValidationErrors } from "../types";
import { ImageCell } from "../template/ImageCellTemplate";

export const useHandleChanges = <T extends Record<string, any>>(
  data: T[],
  validator: (props: any) => Promise<{ newErrors: ValidationErrors }>,
  onDataChange: (data: T[]) => void,
  handleErrors: (newErrors: ValidationErrors) => void
) => {
  const [loading, setLoading] = useState(false);

  const handleChanges = useCallback(async (changes: CellChange<DefaultCellTypes | ImageCell>[]) => {
    setLoading(true);
    try {
      const results = await Promise.all(
        changes.map(change => validator({ change, setTargetCellError }))
      );

      results.forEach(({ newErrors }) => handleErrors(newErrors));

      const newData = applyChangesToData<T>(changes, data);
      onDataChange(newData);
    } catch (err) {
      console.error("Error in handleChanges:", err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, validator, onDataChange, handleErrors]);

  return { loading, handleChanges };
};
